import { useEffect, useState } from 'react';
import {
  resolveNftMedia,
  isVideoUrl,
  gatewayCount,
  NFT_MEDIA_FALLBACK,
} from 'utils/mediaHelpers';
import './index.scss';

/**
 * Resilient NFT image/video renderer.
 * - Resolves IPFS/Pinata URLs through a gateway fallback chain (fixes 403s).
 * - Shows a loading skeleton until the media loads.
 * - On error, advances to the next gateway; when all fail, shows a placeholder.
 *
 * Props: src, alt, className, video (force video), poster, plus any extra
 * attributes forwarded to the underlying <img>/<video>.
 */
const NftMedia = ({
  src,
  alt = 'NFT',
  className = '',
  video,
  poster,
  style,
  imgProps = {},
  videoProps = {},
}) => {
  const [gatewayIndex, setGatewayIndex] = useState(0);
  const [status, setStatus] = useState('loading'); // loading | loaded | error

  const isVideo = video ?? isVideoUrl(src);
  const resolved = resolveNftMedia(src, gatewayIndex);

  useEffect(() => {
    setGatewayIndex(0);
    setStatus(src ? 'loading' : 'error');
  }, [src]);

  const handleError = () => {
    if (gatewayIndex < gatewayCount() - 1) {
      setGatewayIndex((i) => i + 1);
      setStatus('loading');
    } else {
      setStatus('error');
    }
  };

  const handleLoad = () => setStatus('loaded');

  return (
    <span className={`nft-media nft-media--${status} ${className}`} style={style}>
      {status === 'loading' && (
        <span className="nft-media__skeleton" aria-hidden="true" />
      )}

      {status === 'error' ? (
        <img
          className="nft-media__el nft-media__fallback"
          src={NFT_MEDIA_FALLBACK}
          alt={alt}
          draggable={false}
        />
      ) : isVideo ? (
        <video
          key={resolved}
          className="nft-media__el"
          src={resolved}
          poster={poster}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={handleLoad}
          onError={handleError}
          {...videoProps}
        />
      ) : (
        <img
          key={resolved}
          className="nft-media__el"
          src={resolved}
          alt={alt}
          loading="lazy"
          draggable={false}
          onLoad={handleLoad}
          onError={handleError}
          {...imgProps}
        />
      )}
    </span>
  );
};

export default NftMedia;
